import { LeasingCalculatorData } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import {
  getParticipationPropertyByLeasingType,
  getremainingValuePercentageByLeasingType,
} from 'modules/leasingCalculator/components/common/helper';
import { PDV } from 'modules/leasingCalculator/components/infoCalculation/components/common/helper';
import {
  LeasingCalculatorProductSelectionData,
  LeasingObjectAgeCode,
  LeasingPremadeProduct,
  NOT_REGISTERED,
} from 'modules/types/LeasingCalculator';
import {
  CombinedLeasingObjectDataByMake,
  CombinedLeasingObjectTypesAndMakes,
  LeasingObject,
  UwtIdType,
  UwtOptions,
  UwtSupplierType,
  UwtType,
} from 'modules/types/LeasingCalculatorCodes';
import { removeArrayDuplicates, sortObjectsByName } from './mapperHelper';

export const mapUwtTypeToOptions = (
  uwtTypes?: UwtType[] | UwtSupplierType[] | UwtType,
) => {
  if (Array.isArray(uwtTypes)) {
    return uwtTypes.map((uwtType) => ({
      label: uwtType.name,
      value: uwtType.id,
    }));
  }
  if (uwtTypes) {
    return [
      {
        label: uwtTypes.name,
        value: uwtTypes.id,
      },
    ];
  }
  return undefined;
};

export const mapToPercentages = (uwtTypes?: UwtType[]) =>
  uwtTypes?.map((uwtType) => ({
    name: `${uwtType.name}%`,
    id: uwtType.id,
  }));

export const mapToObjectsByObjectTypeId = (
  objectTypeId?: UwtIdType,
  leasingObjectTypesAndMakes?: CombinedLeasingObjectTypesAndMakes[],
): UwtOptions => {
  if (!objectTypeId || !leasingObjectTypesAndMakes) return {};

  const filteredLeasingObjects = leasingObjectTypesAndMakes
    ?.find((item) => item.id === objectTypeId)
    ?.leasingObjects.map((item: LeasingObject) => ({
      id: item.id,
      name: item.name,
    }));
  const sortedLeasingObjects = sortObjectsByName(filteredLeasingObjects);

  return {
    uwtTypes: sortedLeasingObjects,
    options: mapUwtTypeToOptions(sortedLeasingObjects),
  };
};

export const mapToObjectMakesByObjectTypeAndObjectId = (
  objectTypeId?: UwtIdType,
  objectId?: UwtIdType,
  leasingObjectTypesAndMakes?: CombinedLeasingObjectTypesAndMakes[],
): UwtOptions => {
  if (!objectTypeId || !objectId || !leasingObjectTypesAndMakes) return {};

  const filteredObjectMakes = leasingObjectTypesAndMakes
    ?.find((objectType) => objectType.id === objectTypeId)
    ?.leasingObjects.find((object) => object.id === objectId)?.makes;

  const sortedObjectMakes = sortObjectsByName(filteredObjectMakes);

  return {
    uwtTypes: sortedObjectMakes,
    options: mapUwtTypeToOptions(sortedObjectMakes),
  };
};

export const mapToObjectModelsByObjectIds = (
  objectTypeId?: UwtIdType,
  objectId?: UwtIdType,
  makeId?: UwtIdType,
  leasingObjectDataByMake?: CombinedLeasingObjectDataByMake[],
): UwtOptions => {
  if (!makeId || !leasingObjectDataByMake) return {};

  const filteredObjectModels = removeArrayDuplicates(
    leasingObjectDataByMake
      ?.filter(
        (item) =>
          item.makeId === makeId &&
          item.leasingObjectTypeId === objectTypeId &&
          item.leasingObjectId === objectId,
      )
      .map((item) => ({
        id: item.modelId,
        name: item.modelName,
      })),
  );

  const sortedObjectModels = sortObjectsByName(filteredObjectModels);

  return {
    uwtTypes: sortedObjectModels,
    options: mapUwtTypeToOptions(sortedObjectModels),
  };
};

export const mapToFuelsByModelId = (
  modelId?: UwtIdType,
  leasingObjectDataByMake?: CombinedLeasingObjectDataByMake[],
): UwtOptions => {
  if (!modelId || !leasingObjectDataByMake) return {};

  const filteredFuels = removeArrayDuplicates(
    leasingObjectDataByMake
      ?.filter((item) => item.modelId === modelId && item.fuelTypeName !== '')
      .map((item) => ({
        id: item.fuelTypeId,
        name: item.fuelTypeName,
      })),
  );

  const sortedFuels = sortObjectsByName(filteredFuels);

  return {
    uwtTypes: sortedFuels,
    options: mapUwtTypeToOptions(sortedFuels),
  };
};

export const mapToEnginesByModelAndFuelTypeId = (
  modelId?: UwtIdType,
  fuelTypeId?: UwtIdType,
  leasingObjectDataByMake?: CombinedLeasingObjectDataByMake[],
): UwtOptions => {
  if (!modelId || !fuelTypeId || !leasingObjectDataByMake) return {};

  const filteredEngines = removeArrayDuplicates(
    leasingObjectDataByMake
      ?.filter(
        (item) =>
          item.modelId === modelId &&
          item.fuelTypeId === fuelTypeId &&
          item.engine !== '',
      )
      .map((item) => ({
        id: item.engine,
        name: item.engine,
      })),
  );
  const sortedEngines = sortObjectsByName(filteredEngines);

  return {
    uwtTypes: sortedEngines,
    options: mapUwtTypeToOptions(sortedEngines),
  };
};

export const mapToAverageTotalMileageOptionsByObjectData = (
  modelId?: UwtIdType,
  fuelTypeId?: UwtIdType,
  engine?: UwtIdType,
  leasingObjectDataByMake?: CombinedLeasingObjectDataByMake[],
): UwtOptions => {
  if (!modelId || !leasingObjectDataByMake) return {};

  const filteredAverageTotalMileage = removeArrayDuplicates(
    leasingObjectDataByMake
      ?.filter(
        (item) =>
          item.modelId === modelId &&
          item.fuelTypeId === fuelTypeId &&
          item.engine === engine,
      )
      .map((item) => ({
        id: item.averageTotalMileage,
        name: `${item.averageTotalMileage.toString()} km`,
      })),
  );

  return {
    uwtTypes: filteredAverageTotalMileage,
    options: mapUwtTypeToOptions(
      filteredAverageTotalMileage
        ?.slice()
        .sort((a, b) => (a.id > b.id ? 1 : -1)),
    ),
  };
};

export const productSelectionMapper = (
  premadeProduct: LeasingPremadeProduct,
  calculatorData: LeasingCalculatorData,
  leasingObjectAges: UwtType[],
): LeasingCalculatorProductSelectionData => {
  const participationProperty = getParticipationPropertyByLeasingType(
    calculatorData.leasingType?.name,
  );

  const remainingValuePercentage = getremainingValuePercentageByLeasingType(
    calculatorData.leasingType?.name,
  );

  const ageValue = leasingObjectAges.find(
    (ageCode) =>
      ageCode.name ===
      getLeasingObjectAgeCodeByFirstRegistrationYear(
        calculatorData?.leasingObjectFirstRegistrationYear?.name,
      ),
  )?.id as number;

  return {
    leasingType: calculatorData?.leasingType?.id as number,
    client: {
      lesseeTypeId: calculatorData?.leasingRecipientType?.id as number,
    },
    transaction: {
      productId: premadeProduct.productId,
      leasingTypeId: calculatorData?.leasingType?.id as number,
      financingDuration: (calculatorData?.financingPeriod?.id as number) * 12,
      participationPercentage: calculatorData?.[
        `${participationProperty}` as keyof typeof calculatorData
      ] as number,
      redemptionValuePercentage: premadeProduct.minimumRepurchaseValue,
      leasingObjectAmount: Number(calculatorData?.leasingNetObjectPrice) * PDV,
      leasingObjectMakeId: calculatorData?.leasingObjectMake?.id as number,
      leasingObjectModelId: calculatorData?.leasingObjectModel?.id as number,
      leasingObjectTypeId: calculatorData?.leasingObjectType?.id as number,
      ppmvAmount: Number(calculatorData?.leasingObjectPpmv),
      leasingObjectAmountWithoutTaxes: Number(
        calculatorData?.leasingNetObjectPrice,
      ),
      leasingObjectCatalogAmount: Number(calculatorData?.leasingObjectPrice),
      leasingObjectId: calculatorData?.leasingObject?.id as number,
      fuelTypeId: calculatorData?.leasingObjectFuel?.id as number,
      engine: calculatorData?.leasingObjectEngine?.name,
      supplier: calculatorData?.leasingObjectSupplier?.gemicroId,
      productionDate: getProductionDatebyFirstRegistrationYear(
        calculatorData?.leasingObjectFirstRegistrationYear?.name,
      ),
      milage: Number(
        calculatorData?.currentNumberOfKilometersTraveledOrWorkingHours,
      ),
      allowedUsageLevel: calculatorData?.averageTotalMileage?.id as string,
      ageId: ageValue,
      currency: calculatorData.leasingCurrency?.name,
      interestRateTypeId: premadeProduct.interestRateType?.id as number,
      interestRateType: premadeProduct.interestRateType?.name,
      referentInterestRateId: premadeProduct.referentInterestRate?.id as number,
      paymentPeriodId: calculatorData.leasingPaymentPeriodType?.id as number,
      remainingValuePercentage: calculatorData?.[
        `${remainingValuePercentage}` as keyof typeof calculatorData
      ]
        ?.toString()
        ?.replace(',', '.'),
    },
  };
};

export const getLeasingObjectAgeCodeByFirstRegistrationYear = (year?: string) =>
  year === NOT_REGISTERED
    ? LeasingObjectAgeCode.NEW
    : LeasingObjectAgeCode.USED;

export const getProductionDatebyFirstRegistrationYear = (year?: string) => {
  if (!year) return undefined;

  if (year === NOT_REGISTERED) {
    return new Date();
  }

  return new Date(year);
};
